import React, { FC, ReactElement, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import Combo from '../combo/Combo';
import type { SlideProps } from './Slide';
import { SlideElement } from './Slide';

type SlideEditorProps = {
  background: any;
  slideSettings: any;
};

type EditorProps = SlideProps & SlideEditorProps;

const SlideElementEditor = styled(SlideElement)`
  &.slide {
    cursor: pointer;
    outline: 0.5px solid transparent;
    ${(props) =>
      props.isDraggingOver &&
      css`
        background: rgba(63, 162, 254, 0.05);
        border: 1px solid rgba(63, 162, 254, 1);
        cursor: move;
      `};
    ${(props) =>
      props.isDragging &&
      `
          cursor: move;
  `};
    &.activeMode {
      outline: 2px solid #4cba78;
      background: rgba(76, 186, 120, 0.05);
    }
    &:hover {
      outline-color: #4cba78;
      box-shadow: 0px 1px 1px rgba(18, 3, 62, 0.1), 0px 0px 1px rgba(18, 3, 62, 0.1);
      background: none;
      .addSlide {
        display: flex;
      }
    }
    &:last-child {
      margin-bottom: 80px;
    }
    .inner-wrapper {
      ${(props) =>
        props.minHeight === 'screenFit' &&
        !props.isCustomHeight &&
        css`
          min-height: calc(100vh - 182px);
        `};
    }
    .comboWrapper,
    .set,
    .element {
      &.activeMode {
        background: rgba(63, 162, 254, 0.05);
        outline: 3px solid #3fa2fe;
        &:hover {
          background: rgba(63, 162, 254, 0.05);
          outline: 3px solid #3fa2fe;
        }
      }
    }
    ${(props) =>
      props.activeType === 'element' &&
      css`
        .comboWrapper.currentCombo {
          &:hover {
            outline: 1px dashed rgba(63, 162, 254, 0.5) !important;
          }
          .combo {
            &:not(.simpleCombo) {
              .set {
                &:hover {
                  outline: 1px dashed rgba(63, 162, 254, 0.75) !important;
                }
              }
            }
          }
        }
      `}
    ${(props) =>
      props.activeType === 'set' &&
      css`
        &:hover {
          outline: 1px dashed rgba(63, 162, 254, 0.5) !important;
        }
        .comboWrapper.currentCombo {
          &:hover {
            outline: 1px dashed rgba(63, 162, 254, 0.75) !important;
          }
        }
      `}
  ${(props) =>
      props.activeType === 'combo' &&
      css`
        &:hover {
          outline: 1px dashed rgba(63, 162, 254, 0.75) !important;
        }
      `}
  }
`;

const SlideEditor: FC<EditorProps> = ({ ...props }): ReactElement => {
  const slideBackground = props.background;
  const useAppSelector = props.editorPlugins.useAppSelector;
  const useAppDispatch = props.editorPlugins.useAppDispatch;
  const selectActive = props.editorPlugins.selectActive;
  const setActiveType = props.editorPlugins.setActiveType;
  const setActiveProps = props.editorPlugins.setActiveProps;
  const selectActiveSet = props.editorPlugins.selectActiveSet;
  const selectActiveSettings = props.editorPlugins.selectActiveSettings;
  const thumbnailActivePath = props.editorPlugins.thumbnailActivePath;
  const setSettingsTitle = props.editorPlugins.setSettingsTitle;
  const updateData = props.editorPlugins.updateData;
  const Duplicate = props.editorPlugins.duplicateButton;
  const Delete = props.editorPlugins.deleteButton;
  const CopyToClipboard = props.editorPlugins.copyToClipboard;
  const Droppable = props.editorPlugins.droppable;
  const scrollTo = props.editorPlugins.scrollTo;
  const trackStory = props.editorPlugins.trackStory;
  const addUniqueKeysToSlide = props.editorPlugins.addUniqueKeysToSlide;
  const getElementSettings = props.editorPlugins.getElementSettings;
  const OutlineSetting = props.editorPlugins.OutlineSetting;
  const environmentLink = window.getEnvironmentLink();

  const activePath: any = useAppSelector((state: any) => state.editor.present.activePath);
  const activeType: string = useAppSelector((state: any) => state.editor.present.activeType);
  const globalData: any | null = useAppSelector((state: any) => state.editor.present.data);
  const activeThumbnailPath: any = useAppSelector(
    (state: any) => state.editor.present.activeThumbnailPath,
  );
  const isStorydocAdmin: boolean = useAppSelector(
    (state: any) => state.editor.present.isStorydocAdmin,
  );
  const dispatch = useAppDispatch();
  const settings = props.slideSettings;
  const slideTag = props.slideActiveIndex || 1;
  const slideRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  let emptyComboData = {
    name: 'Empty',
    settings: {
      verticalAlignment: 'inherit',
      horizontalAlignment: 'inherit',
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      borderWidth: 1,
      round: '8px',
      minHeight: 'auto',
      isCustomHeight: false,
      maxWidth: 'large',
      isCustomWidth: false,
      setsPerLine: '1',
      setsSpace: '16',
    },
    sets: [
      {
        horizontalAlignment: 'inherit',
        verticalAlignment: 'inherit',
        groupsRatio: ['100', '100'],
        groups: [[]],
      },
    ],
  };

  useEffect(() => {
    let isActive = activePath.slideIndex === props.index && activePath.comboIndex === null;
    if (isActive) dispatch(setActiveProps(props.settings));
    const observer = new IntersectionObserver(checkVisibility, { threshold: 0.1 });
    const slideCurrent = slideRef && slideRef.current;
    if (slideCurrent) {
      observer.observe(slideCurrent);
    }

    return () => {
      if (slideCurrent) {
        observer.unobserve(slideCurrent);
      }
    };
  }, []);

  function checkVisibility(entries: any) {
    const [entry] = entries;
    if (entry.isIntersecting) {
      let path = {
        slideIndex: props.index,
        comboIndex: null,
        setIndex: null,
        groupIndex: null,
        elementIndex: null,
      };
      if (window.disableScroll) return;
      dispatch(thumbnailActivePath(path));
    }
  }

  function getComboData(combo: any) {
    let comboData;
    if (combo.name === 'VTimeline') {
      comboData = { ...combo };
      let comboSettings;
      let comboExtraFields = {};

      comboExtraFields = {
        comboDotsColor: combo.settings.comboDotsColor,
        comboDotsColorActive: combo.settings.comboDotsColorActive,
        comboLineColor: combo.settings.comboLineColor,
      };
      comboSettings = {
        ...combo.settings,
        ...comboExtraFields,
      };
      comboData.settings = comboSettings;
    }
    return comboData ? comboData : combo;
  }

  function handleClick(event: any) {
    event.preventDefault();
    if (
      event.target === event.currentTarget ||
      event.target.classList.contains('slideBackground') ||
      event.target.parentElement?.classList.contains('slideBackground')
    ) {
      dispatch(
        trackStory({
          event: 'click on slide',
          screen: 'editorv4',
          params: { editorVersion: 'V4' },
        }),
      );
      return selectActiveSlide();
    }
  }

  function getPath(index: number) {
    if (!props.path) return null;
    let path = { ...props.path };
    path.comboIndex = index;
    return path;
  }

  function isHiddenSlide() {
    if (!globalData || props.index === undefined || props.index === null) return;
    return globalData.slides[props.index].settings.isHidden;
  }

  function selectActiveSlide() {
    setActiveAction();
    dispatch(
      trackStory({
        event: 'success active slide',
        screen: 'editorv4',
        params: { editorVersion: 'V4' },
      }),
    );
    dispatch(setSettingsTitle('Slide'));
    return dispatch(selectActive(props.path));
  }

  function setActiveAction() {
    dispatch(setActiveType('slide'));
    dispatch(setActiveProps(props.settings));
    settings ? dispatch(selectActiveSettings({ settings })) : dispatch(selectActiveSettings(null));
  }

  //   function addSlide(event: any) {
  //     event.stopPropagation();
  //     dispatch(thumbnailActivePath({ ...props.path }));
  //     dispatch(
  //       trackStory({
  //         event: 'click add slide from main container',
  //         screen: 'editorv4',
  //         params: { editorVersion: 'V4' },
  //       }),
  //     );
  //   }

  function duplicateSlide() {
    dispatch(
      trackStory({
        event: 'click duplicate slide from main container',
        screen: 'editorv4',
        params: { editorVersion: 'V4' },
      }),
    );
    let slides = [...globalData.slides];
    slides.splice(
      activePath.slideIndex + 1,
      0,
      addUniqueKeysToSlide
        ? addUniqueKeysToSlide(props, { forceAdd: true })
        : activePath.slideIndex + 1,
    );
    let data = {
      path: activePath,
      data: slides,
      object: 'slide',
    };
    dispatch(updateData(data));
    dispatch(selectActive({ ...activePath, slideIndex: activePath.slideIndex + 1 }));
    setTimeout(() => scrollToSlide(activePath.slideIndex + 1), 500);
  }

  function deleteSlide() {
    dispatch(
      trackStory({
        event: 'click delete slide from main container',
        screen: 'editorv4',
        params: { editorVersion: 'V4' },
      }),
    );
    let slides = [...globalData.slides];
    slides.splice(activePath.slideIndex, 1);
    let data = {
      path: activePath,
      data: slides,
      object: 'slide',
    };
    dispatch(updateData(data));
    let newPath = { ...activePath };
    if (slides.length === 1) {
      newPath.slideIndex = 0;
    } else if (activePath.slideIndex === slides.length) {
      newPath.slideIndex = activePath.slideIndex - 1;
    }
    dispatch(selectActive(newPath));
    setTimeout(() => scrollToSlide(newPath.slideIndex), 500);
  }

  function scrollToSlide(index: number) {
    let slides = document.querySelectorAll('.mainStory .slide');
    let slide = slides[index] as HTMLElement;
    if (slide) scrollTo(slide, 'top');
  }

  //   function addCombo() {
  //     dispatch(
  //       trackStory({
  //         event: 'click add element/combo from slide',
  //         screen: 'editorv4',
  //         params: { editorVersion: 'V4' },
  //       }),
  //     );
  //     let path = { ...props.path };
  //     path.comboIndex =
  //       path.comboIndex === null || path.comboIndex === undefined ? 0 : path.comboIndex++;
  //     let data = {
  //       path: path,
  //       type: 'combo',
  //     };
  //     dispatch(selectActiveSet(''));
  //     return dispatch(openElementsLibrary(data));
  //   }

  function saveToMySlides(slideObj: any) {
    const slide = {
      settings: slideObj.settings,
      combos: slideObj.combos,
    };
    props.editorPlugins.angularObservable.saveToMySlides$.next(slide);
  }

  function openAddSlide() {
    dispatch(
      trackStory({
        event: 'click add new slide',
        screen: 'editorv4',
        params: { editorVersion: 'V4' },
      }),
    );
    let index = activeThumbnailPath.slideIndex !== null ? activeThumbnailPath.slideIndex : 0;
    props.editorPlugins.angularObservable.addSlide$.next(index);
  }

  const isSlideActive =
    activePath && activePath.slideIndex === props.index && activePath.comboIndex === null;

  return (
    <Droppable droppableId={props.path?.slideIndex.toString()}>
      {(provided: any, snapshot: any) => (
        <SlideElementEditor
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={
            'slide editable ' +
            (isSlideActive ? 'activeMode ' : '') +
            (isHiddenSlide() ? 'hiddenSlide' : '')
          }
          verticalAlignment={props.settings.verticalAlignment}
          horizontalAlignment={props.settings.horizontalAlignment}
          minHeight={props.settings.minHeight}
          isCustomHeight={props.settings.isCustomHeight}
          background={slideBackground}
          index={props.index}
          isCustomWidth={props.settings.isCustomWidth}
          isDraggingOver={snapshot.isDraggingOver}
          isDragging={snapshot.isDragging}
          data-anchor-target={props.settings.isAnchorActive ? props.settings.anchorName : ''}
          activeType={activeType}
          onClick={(event) => {
            handleClick(event);
          }}>
          <div className="slide__controls">
            <div className={'slideTag ' + (activePath.slideIndex === props.index ? 'active' : '')}>
              {'Slide ' + slideTag}
            </div>
            <div className="setting__controls">
              <div
                className="background"
                onClick={() => dispatch(selectActiveSet('Change Background'))}>
                <img
                  src={environmentLink + '/assets/images/Slide/background.svg'}
                  alt="Background"
                  width="16"
                  height="16"
                />
                Background
              </div>
              <div
                className="overlay"
                onClick={() => dispatch(selectActiveSet('Background overlay color'))}>
                <img
                  src={environmentLink + '/assets/images/Slide/overlay.svg'}
                  alt="Overlay"
                  width="16"
                  height="16"
                />
                Overlay
              </div>
            </div>
          </div>
          {props.index === 0 && (
            <div className="addSlide top" onClick={() => openAddSlide()}>
              <p>Add Slide</p>
            </div>
          )}
          <div className="slideBackground">
            {props.settings.backgroundType &&
              props.settings.backgroundType.toLowerCase() === 'image' &&
              props.settings.backgroundImage && (
                <img src={props.settings.backgroundImage} alt="Slide Background" />
              )}
            {props.settings.backgroundType &&
              props.settings.backgroundType.toLowerCase() === 'video' &&
              props.settings.backgroundVideo && (
                <video autoPlay loop muted key={props.settings.backgroundVideo}>
                  <source src={props.settings.backgroundVideo} type="video/mp4" />
                </video>
              )}
          </div>
          <div className="inner-wrapper" ref={slideRef}>
            {!props.combos.length && (
              <div className={'emptySlide'}>
                <Combo
                  key={0}
                  comboData={emptyComboData}
                  editorPlugins={props.editorPlugins}
                  index={0}
                  path={getPath(0)}
                  isEmptySlide={true}
                />
              </div>
            )}
            {props.combos.map((combo: any, index: number) => {
              let comboData = getComboData(combo);
              return (
                <Combo
                  key={index}
                  comboData={comboData}
                  editorPlugins={props.editorPlugins}
                  index={index}
                  path={getPath(index)}
                  isEmptySlide={false}
                />
              );
            })}
          </div>
          <div className="options">
            <div className={'permanentOptions'}>
              <div
                className={
                  'option addToMySlides ' + (globalData.slides.length === 1 ? 'hidden' : '')
                }
                onClick={() => {
                  saveToMySlides(props);
                }}>
                <div className="mySlideIcon">
                  <img src={environmentLink + '/assets/images/mySlide.svg'} alt="My Slides" />
                </div>
              </div>
              {isStorydocAdmin && (
                <div className={'option copy'}>
                  <CopyToClipboard settings={props.settings} combos={props.combos} />
                </div>
              )}
              <div
                className={'option duplicate ' + (globalData.slides.length === 1 ? 'noAfter' : '')}
                onClick={() => {
                  duplicateSlide();
                }}>
                <Duplicate isOption={true} />
              </div>
              <div
                className={'option delete ' + (globalData.slides.length === 1 ? 'hidden' : '')}
                onClick={() => {
                  deleteSlide();
                }}>
                <Delete isOption={true} />
              </div>
            </div>
          </div>
          <div className="addSlide" onClick={() => openAddSlide()}>
            <p>Add Slide</p>
          </div>
          {provided.placeholder}

          {isHiddenSlide() && (
            <div className="hidden">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_3999_254949)">
                  <path
                    d="M3.01335 3.95601L0.928687 1.87201L1.87202 0.92868L15.0714 14.1287L14.128 15.0713L11.9214 12.8647C10.7488 13.6084 9.38851 14.0022 8.00002 14C4.40535 14 1.41469 11.4133 0.787354 8.00001C1.07404 6.44716 1.85511 5.02861 3.01402 3.95601H3.01335ZM9.83802 10.7813L8.86202 9.80535C8.48872 9.98396 8.0692 10.0424 7.66131 9.97254C7.25341 9.9027 6.87722 9.70806 6.5846 9.41543C6.29198 9.12281 6.09733 8.74662 6.0275 8.33873C5.95766 7.93083 6.01608 7.51132 6.19469 7.13801L5.21869 6.16201C4.7945 6.80301 4.60487 7.5709 4.68191 8.33567C4.75895 9.10044 5.09792 9.81509 5.64143 10.3586C6.18494 10.9021 6.89959 11.2411 7.66436 11.3181C8.42913 11.3952 9.19703 11.2055 9.83802 10.7813ZM5.31602 2.50668C6.14735 2.18001 7.05335 2.00001 8.00002 2.00001C11.5947 2.00001 14.5854 4.58668 15.2127 8.00001C15.0084 9.11048 14.5493 10.1584 13.8714 11.0613L11.298 8.48801C11.3745 7.97309 11.3292 7.44742 11.166 6.95312C11.0027 6.45882 10.7259 6.00962 10.3578 5.64153C9.98975 5.27343 9.54055 4.99666 9.04625 4.8334C8.55195 4.67013 8.02628 4.62491 7.51135 4.70135L5.31602 2.50735V2.50668Z"
                    fill="#88819F"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_3999_254949">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              Hidden slide
            </div>
          )}
          {isSlideActive && <OutlineSetting type="slide" {...props?.settings} />}
        </SlideElementEditor>
      )}
    </Droppable>
  );
};

export default SlideEditor;
